import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoHideScrollbar } from '../../hooks/useAutoHideScrollbar';
import { ArtPreview } from '../ArtPreview';
import { PreviewArt } from '../../../../shared/campaign/PreviewArt';
import ImagesScrollableContainer from '../../../../shared/campaign/ImagesScrollableContainer';
import { useCampaignRuntimeContext } from '../../context/CampaignRuntimeContext';
import { useAgentChatContext } from '../../context/AgentChatContext';
import useAgentSessionStore from '../../store/agentSessionStore';
import { resolveSessionPreviewData, isSessionReadOnly } from '../../domain/campaignStateSelectors';
import { resolveEditingPieceNumbers } from '../../domain/artPreviewEditing';
import { usePaywall } from '../../../../contexts/PaywallContext/usePaywall';
import { getArtGenerationAvailability } from '../../../../utils/artConsumption';
import { notifyErrorCustom } from '../../../../utils/notify';
import {
  updateSessionStrategy,
  editArt,
  persistPieceLegend,
  updatePieceSchedule,
} from '../../services/agentSessionService';
import {
  buildScheduleFieldsFromArt,
  getArtScheduleKey,
  isCompleteScheduleDateTimeFields,
  scheduleFieldsDifferFromCanonical,
} from '../../../../shared/campaign/utils/scheduleDateTime';
import styles from './styles.module.css';

export default function Canvas({
  content,
  strategyFromSession = '',
  selectedArt,
  setSelectedArt,
  imageRenderKey = 0,
}) {
  const previewScrollRef = useRef(null);
  const { ref: previewScrollContainerRef, scrollClassName: previewScrollClassName } =
    useAutoHideScrollbar(previewScrollRef);
  const { refetch, sessionDetail, messages } = useAgentChatContext();
  const activeSessionId = useAgentSessionStore((s) => s.activeSessionId);
  const {
    runtime,
    schedulePreviewRequest,
    setSchedulePreviewRequest,
    artScheduleData,
    updateArtScheduleField,
    ensureArtScheduleEntry,
    scheduleUx,
    clearSchedulePreviewSelection,
  } = useCampaignRuntimeContext();
  const [activeTab, setActiveTab] = useState('estrategia');
  const [previewInitialTab, setPreviewInitialTab] = useState('ai');
  const legendDebounceRef = useRef(null);
  const scheduleDebounceRef = useRef(null);
  const latestScheduleFieldsRef = useRef({});
  const { openPaywall, closePaywall } = usePaywall();
  const { t } = useTranslation();

  const openArtLimitPaywall = useCallback(() => {
    openPaywall({
      reason: 'art_generation_limit_reached',
      returnTo: window.location.pathname,
      onDismiss: async () => {
        window.dispatchEvent(new CustomEvent('user-plan-count-updated'));
        closePaywall();
      },
    });
  }, [openPaywall, closePaywall]);

  useEffect(() => {
    if (!schedulePreviewRequest?.art) {
      return;
    }
    const { art, initialTab = 'edit' } = schedulePreviewRequest;
    setActiveTab('artes');
    setSelectedArt(art);
    setPreviewInitialTab(initialTab);
    setSchedulePreviewRequest(null);
  }, [schedulePreviewRequest, setSchedulePreviewRequest, setSelectedArt]);

  const previewData = resolveSessionPreviewData({
    messages,
    strategyFromSession,
    strategyMarkdown: content ?? runtime.content.strategy,
    artResults: runtime.content.arts,
    artStatus: runtime.content.artStatus,
    artProgress: runtime.content.artProgress,
    sessionDetail,
  });

  useEffect(() => {
    if (
      previewData.showArtsTab &&
      (previewData.artStatus === 'generating' || previewData.artStatus === 'editing')
    ) {
      setActiveTab('artes');
    }
  }, [previewData.showArtsTab, previewData.artStatus]);

  const editingPieceNumbers = useMemo(
    () => resolveEditingPieceNumbers(runtime.operation),
    [runtime.operation],
  );

  useEffect(() => {
    if (!selectedArt) {
      return;
    }
    const pieceNumber = selectedArt.pieceNumber ?? selectedArt.piece_number;
    const index = previewData.artResults.findIndex(
      (item) => (item.pieceNumber ?? item.piece_number) === pieceNumber,
    );
    ensureArtScheduleEntry(selectedArt, index >= 0 ? index : 0);
  }, [selectedArt, previewData.artResults, ensureArtScheduleEntry]);

  useEffect(() => {
    if (!selectedArt) {
      return;
    }
    const pieceNumber = selectedArt.pieceNumber ?? selectedArt.piece_number;
    const updated = previewData.artResults.find(
      (item) => (item.pieceNumber ?? item.piece_number) === pieceNumber,
    );
    if (!updated) {
      return;
    }
    const previousUrl = selectedArt.url ?? selectedArt.imageUrl;
    const previousRevision = selectedArt.revision ?? null;
    const previousDateTime = selectedArt.publicationDateTime ?? null;
    const nextUrl = updated.url ?? updated.imageUrl;
    const nextRevision = updated.revision ?? null;
    const nextDateTime = updated.publicationDateTime ?? null;
    if (
      previousUrl !== nextUrl ||
      previousRevision !== nextRevision ||
      previousDateTime !== nextDateTime
    ) {
      setSelectedArt(updated);
    }
  }, [previewData.artResults, selectedArt, setSelectedArt]);

  const showArts = previewData.showArtsTab;
  const showStrategy = !!previewData.strategyMarkdown;
  const sessionReadOnly = isSessionReadOnly(sessionDetail);
  const isCalendarPreviewEnabled = scheduleUx?.isCalendarPreviewEnabled ?? false;
  const isScheduleReadOnly = scheduleUx?.isPublicationReadOnly ?? true;
  const isStrategyCanvasEditBlocked = useMemo(
    () => runtime.input.disabled,
    [runtime.input.disabled],
  );
  const canEditStrategy =
    !isStrategyCanvasEditBlocked &&
    previewData.artResults.length === 0 &&
    !sessionReadOnly;

  const handleStrategySave = useCallback(
    async (markdown) => {
      if (!activeSessionId) return;
      await updateSessionStrategy(activeSessionId, markdown);
      refetch();
    },
    [activeSessionId, refetch],
  );

  const handleEditRequest = useCallback(
    async (art, instruction, imageUrls) => {
      const pieceNumber = art?.pieceNumber ?? art?.piece_number;
      if (!activeSessionId || !pieceNumber || !instruction?.trim()) {
        return;
      }

      const availability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      });
      if (!availability.hasAvailableArts) {
        openArtLimitPaywall();
        return;
      }

      const referenceImageUrls = (imageUrls || [])
        .slice(1)
        .filter(
          (url) =>
            typeof url === 'string' &&
            url.trim().length > 0 &&
            (url.startsWith('http') || url.startsWith('data:')),
        );

      try {
        await editArt(activeSessionId, {
          pieceNumber,
          editInstruction: instruction.trim(),
          referenceImageUrls,
        });
        await refetch();
      } catch (error) {
        const message =
          error?.response?.data?.error?.message ||
          error?.message ||
          t('campaignChat.errors.editArtFailed', 'Não foi possível editar a arte.');
        notifyErrorCustom(message);
      }
    },
    [activeSessionId, openArtLimitPaywall, refetch, t],
  );

  const handleScheduleDataChange = useCallback(
    (field, value, pieceNumber) => {
      if (!pieceNumber) {
        return;
      }

      updateArtScheduleField(pieceNumber, field, value);

      if (field === 'caption' && activeSessionId) {
        clearTimeout(legendDebounceRef.current);
        legendDebounceRef.current = setTimeout(() => {
          persistPieceLegend(activeSessionId, pieceNumber, value)
            .then(() => refetch())
            .catch(() => undefined);
        }, 650);
        return;
      }

      if (
        (field === 'publishDate' || field === 'publishTime') &&
        activeSessionId
      ) {
        const currentFields = latestScheduleFieldsRef.current[pieceNumber] || {};
        latestScheduleFieldsRef.current[pieceNumber] = {
          ...currentFields,
          [field]: value,
        };

        clearTimeout(scheduleDebounceRef.current);
        scheduleDebounceRef.current = setTimeout(() => {
          const { publishDate, publishTime } =
            latestScheduleFieldsRef.current[pieceNumber] || {};
          if (!isCompleteScheduleDateTimeFields(publishDate, publishTime)) {
            return;
          }

          updatePieceSchedule(activeSessionId, pieceNumber, {
            publishDate,
            publishTime,
          })
            .then(() => refetch())
            .catch(() => undefined);
        }, 650);
      }
    },
    [activeSessionId, refetch, updateArtScheduleField],
  );

  useEffect(
    () => () => {
      clearTimeout(legendDebounceRef.current);
      clearTimeout(scheduleDebounceRef.current);
    },
    [],
  );

  const selectedScheduleData = useMemo(() => {
    if (!selectedArt) {
      return {};
    }
    const artKey = getArtScheduleKey(selectedArt);
    const baseFields = buildScheduleFieldsFromArt(selectedArt);
    const editedFields = artScheduleData[artKey] || {};
    return {
      ...baseFields,
      ...editedFields,
      caption:
        editedFields.caption !== undefined
          ? editedFields.caption
          : baseFields.caption,
      publishDate:
        editedFields.publishDate !== undefined
          ? editedFields.publishDate
          : baseFields.publishDate,
      publishTime:
        editedFields.publishTime !== undefined
          ? editedFields.publishTime
          : baseFields.publishTime,
      selectedNetworks:
        editedFields.selectedNetworks !== undefined
          ? editedFields.selectedNetworks
          : baseFields.selectedNetworks,
    };
  }, [selectedArt, artScheduleData]);

  useEffect(() => {
    if (!selectedArt) {
      return;
    }
    const pieceNumber = selectedArt.pieceNumber ?? selectedArt.piece_number;
    if (!pieceNumber) {
      return;
    }
    latestScheduleFieldsRef.current[pieceNumber] = {
      publishDate: selectedScheduleData.publishDate || '',
      publishTime: selectedScheduleData.publishTime || '',
    };
  }, [selectedArt, selectedScheduleData.publishDate, selectedScheduleData.publishTime]);

  const scheduleSyncWarning = useMemo(() => {
    if (!selectedArt) {
      return false;
    }
    const artKey = getArtScheduleKey(selectedArt);
    const editedFields = artScheduleData[artKey] || {};
    return scheduleFieldsDifferFromCanonical(editedFields, selectedArt);
  }, [selectedArt, artScheduleData]);

  if (!showStrategy && !showArts) {
    return null;
  }

  return (
    <div className={styles.canvas}>
      {(showStrategy || showArts) && (
        <div className={styles.previewHeader}>
          <p className={styles.previewHeaderText}>Preview</p>
          <div className={styles.previewHeaderTabsContainer}>
            <div className={styles.previewHeaderTabs}>
              <button
                type="button"
                className={`${styles.previewTab} ${activeTab === 'artes' ? styles.previewTabActive : ''}`}
                onClick={() => showArts && setActiveTab('artes')}
                disabled={!showArts}
              >
                Artes
              </button>
              <button
                type="button"
                className={`${styles.previewTab} ${activeTab === 'estrategia' ? styles.previewTabActive : ''}`}
                onClick={() => setActiveTab('estrategia')}
              >
                Estratégia
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        ref={previewScrollContainerRef}
        className={`${styles.markdownWrapper} ${previewScrollClassName}`}
      >
        {activeTab === 'artes' && showArts && (
          <>
            {selectedArt ? (
              <PreviewArt
                images={previewData.artResults}
                totalImages={
                  previewData.artProgress.total || previewData.artResults.length
                }
                imageRenderKey={imageRenderKey}
                sessionDetail={sessionDetail}
                selectedImage={selectedArt}
                onSelectImage={setSelectedArt}
                onClose={() => {
                  clearSchedulePreviewSelection();
                  setSelectedArt(null);
                }}
                onEditRequest={handleEditRequest}
                editingPieceNumbers={editingPieceNumbers}
                canUseAI={!sessionReadOnly}
                defaultTab={previewInitialTab}
                isCalendarTabEnabled={isCalendarPreviewEnabled}
                isScheduleReadOnly={isScheduleReadOnly}
                scheduleData={selectedScheduleData}
                scheduleSyncWarning={scheduleSyncWarning}
                onScheduleDataChange={(field, value) =>
                  handleScheduleDataChange(
                    field,
                    value,
                    selectedArt?.pieceNumber ?? selectedArt?.piece_number,
                  )
                }
              />
            ) : (
              <ImagesScrollableContainer
                images={previewData.artResults}
                totalImages={
                  previewData.artProgress.total || previewData.artResults.length
                }
                renderKey={imageRenderKey}
                sessionDetail={sessionDetail}
                editingPieceNumbers={editingPieceNumbers}
                onSelectImage={(image) => {
                  setPreviewInitialTab(isCalendarPreviewEnabled ? 'edit' : 'ai');
                  setSelectedArt(image);
                }}
                imageWrapperClassName={styles.canvasImageWrapper}
                scrollableImageClassName={styles.canvasScrollableImage}
                hideImageDetails
              />
            )}
          </>
        )}

        {activeTab === 'estrategia' && showStrategy && (
          <ArtPreview
            artResults={previewData.artResults}
            artStatus={previewData.artStatus}
            artProgress={previewData.artProgress}
            editingPieceNumbers={editingPieceNumbers}
            strategyMarkdown={previewData.strategyMarkdown}
            showArtsTab={showArts}
            activeTab={activeTab}
            selectedImage={selectedArt}
            onSelectImage={setSelectedArt}
            imageRenderKey={imageRenderKey}
            sessionDetail={sessionDetail}
            scheduleData={selectedScheduleData}
            scheduleSyncWarning={scheduleSyncWarning}
            onStrategySave={canEditStrategy ? handleStrategySave : undefined}
            isStrategyEditDisabled={!canEditStrategy}
            onScheduleDataChange={handleScheduleDataChange}
            isScheduleReadOnly={isScheduleReadOnly}
            isCalendarTabEnabled={isCalendarPreviewEnabled}
          />
        )}
      </div>
    </div>
  );
}
